import type { ReadonlyDeep } from 'type-fest';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { HomepageTrendingSection } from './api.graphql';
import type {
	HomepageFeaturedTopicsFragment,
	HomepageTrendingSectionQuery,
	HomepageTrendingSectionQueryVariables,
} from './api.graphql';

type TrendingPetition = HomepageTrendingSectionQuery['trendingPetitions']['edges'][number];

export type TrendingSectionData = {
	trendingPetitions: ReadonlyDeep<TrendingPetition[]>;
	trendingTopics: readonly HomepageFeaturedTopicsFragment[];
};

type GetTrendingSectionDataParams = { locale: string; countryCode: string };

export async function getTrendingSectionData(
	{ gql: { fetch } }: UtilityContext,
	{ countryCode, locale }: GetTrendingSectionDataParams,
): Promise<TrendingSectionData> {
	// Non-critical homepage section (the component renders nothing on error/empty data) — both
	// fields already degrade quietly on a genuine backend failure at the resolver level (see
	// petitionsConnection's `failSilently` and featuredTopicsByLocale's own catch), so this call
	// site needs no error handling of its own.
	const { data } = await fetch<HomepageTrendingSectionQuery, HomepageTrendingSectionQueryVariables>({
		query: HomepageTrendingSection,
		variables: {
			locale,
			countryCode,
		},
		rejectOnError: true,
	});

	return {
		trendingPetitions: data.trendingPetitions.edges,
		trendingTopics: data.trendingTopics.success ? (data.trendingTopics.featuredTopics ?? []) : [],
	};
}

getTrendingSectionData.queryKey = 'HOMEPAGE_TRENDING_SECTION';
